import { ReactElement, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  makeStyles,
  Divider,
  Link,
  Button,
  CircularProgress,
  useTheme,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ReactMarkdown from 'react-markdown';
import { FontFamily } from '@principal-developer-portal/ui';
import {
  ContentCardProps,
  DefaultRemediationStepsProps,
} from './types/content-props';
import { LogicalAccordion, ResourceBasedAccordion } from './insights-accordion';
import SparklesIcon from '../img/sparkles.png';
import { AutofixV2Component } from './autofix-v2-component';
import { YardstickAutoFix } from '@internal/yardstick-common';
import {
  featureFlagsApiRef,
  useAnalytics,
  useApi,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { yardstickApiRef } from '../../../api/yardstick-api';
import { useAtom } from 'jotai';
import { deferStateAtom, DeferState } from '../../../hooks/use-filters';
import { useDeferEligibility } from '../../../hooks/use-defer-eligibility';
import { shouldShowRemediation } from '../../../feature-flags/sca-remediation';

// eslint-disable-next-line max-lines-per-function
const useStyles = makeStyles(theme => ({
  rootContainer: {
    width: 'auto',
    height: 'auto',
    gap: '16px',
    display: 'flex',
    flexDirection: 'column',
  },
  tableBoxContainer: {
    width: 'auto',
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'row',
    overflowY: 'hidden',
    overflowX: 'auto',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-track': {
      background: '#E8E8E8',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb': {
      background: '#9D9EA4',
      borderRadius: '50px',
      paddingBottom: '32px',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  },
  contentBoxContainer: {
    width: '100%',
    height: 'auto',
    maxHeight: '480px',
    gap: '16px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-track': {
      background: '#E8E8E8',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb': {
      background: '#9D9EA4',
      borderRadius: '50px',
      paddingBottom: '32px',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  },
  issueDescription: {
    width: '100%',
    flex: '0 1 auto',
    paddingRight: '16px',
    gap: '8px',
    borderBottomWidth: '1px',
  },
  issueDetails: {
    width: 'auto',
    fontFamily: FontFamily.Bold,
    fontWeight: 400,
    fontSize: '24px',
    lineHeight: '36px',
    paddingRight: 'inherit',
  },
  issueDescriptionDetail: {
    width: 'auto',
    fontFamily: FontFamily.Regular,
    fontSize: '16px',
    lineHeight: '26px',
    paddingRight: 'inherit',
    paddingBottom: '16px',
  },
  actionSteps: {
    width: '100%',
    flex: '1 1 auto',
    gap: '16px',
    borderBottomWidth: '1px',
    paddingRight: '16px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '480px',
  },
  remediationSteps: {
    width: 'auto',
    fontFamily: FontFamily.Bold,
    fontWeight: 400,
    fontSize: '24px',
    lineHeight: '36px',
    paddingRight: '16px',
  },
  remediationDescription: {
    width: 'auto',
    fontFamily: FontFamily.Regular,
    fontSize: '16px',
    lineHeight: '26px',
    paddingRight: '16px',
    paddingBottom: '16px',
  },
  remediationHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '24px',
    marginBottom: '16px',
  },
  budgetOwnerNote: {
    display: 'flex',
    alignItems: 'center',
    // @ts-expect-error custom color
    backgroundColor: theme.palette.lightBackground,
    gap: '8px',
    justifyContent: 'center',
    borderRadius: '4px',
    padding: '10px 16px',
    marginBottom: '8px',
  },
  budgetOwnerNoteError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: theme?.palette?.error?.main,
    borderRadius: '4px',
    padding: '10px 16px',
    marginBottom: '8px',
  },
  budgetOwnerNoteText: {
    fontFamily: FontFamily.Bold,
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '20px',
  },
  customTabsContainer: {
    display: 'flex',
    gap: '0px',
  },
  customTab: {
    padding: '12px 16px',
    cursor: 'pointer',
    fontSize: '16px',
    fontFamily: FontFamily.Bold,
    fontWeight: 600,
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    // Inactive tab - use link color (theme-sensitive)
    color: theme.palette.link,
    transition: 'all 0.2s ease',
    outline: 'none',
    position: 'relative',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.04)',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.link}`,
      outlineOffset: '2px',
    },
  },
  customTabActive: {
    // Active tab - use primary text color
    color: theme.palette.type === 'dark' ? '#FFFFFF' : '#000000',
    borderBottomColor: theme.palette.type === 'dark' ? '#FFFFFF' : '#000000',
    fontFamily: FontFamily.Bold,
    fontWeight: 600,
  },
  customTabWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  customTabIcon: {
    width: '16px',
    height: '16px',
  },
  tabPanel: {
    paddingTop: '8px',
    flex: '1 1 auto',
  },
  tabWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  tabIcon: {
    width: '16px',
    height: '16px',
  },
}));

// Helper: Reusable ReactMarkdown link renderer
const markdownLinkRenderer = {
  a: (linkProps: any) => (
    <Link
      href={linkProps.href}
      component="a"
      color="primary"
      target="_blank"
      rel="noopener noreferrer"
    >
      {linkProps.children}
    </Link>
  ),
};

// Helper: Create a markdown link renderer that intercepts defer links as buttons
const createMarkdownLinkRenderer = (
  onDeferClick?: (deferEndpoint: string) => void,
  deferState: DeferState = 'idle',
  theme?: any,
  deferEligible?: boolean | null,
  catalogError?: boolean,
) => ({
  a: (linkProps: any) => {
    const href = linkProps.href?.trim() ?? '';
    if (onDeferClick && /\/accounts\/[^/]+\/defer$/.test(href)) {
      // Eligibility still loading or catalog error (unknown state — show spinner)
      if (deferEligible === null || catalogError) {
        return (
          <CircularProgress
            size={16}
            style={{
              marginLeft: '4px',
              marginRight: '4px',
              verticalAlign: 'middle',
            }}
          />
        );
      }
      // Not eligible — show disabled button only (note shown at top of remediation steps)
      if (deferEligible === false) {
        return (
          <Button
            variant="outlined"
            size="small"
            disabled
            style={{ borderRadius: '20px' }}
          >
            {linkProps.children}
          </Button>
        );
      }
      if (deferState === 'loading') {
        return (
          <CircularProgress
            size={18}
            style={{
              marginLeft: '4px',
              marginRight: '4px',
              verticalAlign: 'middle',
            }}
          />
        );
      }
      if (deferState === 'not-authorized') {
        return (
          <Button
            variant="outlined"
            size="small"
            disabled
            style={{
              marginLeft: '4px',
              marginRight: '4px',
              verticalAlign: 'middle',
              borderRadius: '20px',
              borderColor: theme?.palette?.error?.main,
              color: theme?.palette?.error?.main,
            }}
          >
            Not authorized to defer
          </Button>
        );
      }
      if (deferState === 'error') {
        return (
          <Button
            variant="contained"
            size="small"
            onClick={() => onDeferClick(href)}
            style={{
              marginLeft: '4px',
              marginRight: '4px',
              verticalAlign: 'middle',
              backgroundColor: theme?.palette?.error?.main,
              color: theme?.palette?.error?.contrastText,
              borderRadius: '20px',
            }}
          >
            Retry Defer for 90 days
          </Button>
        );
      }
      return (
        <Button
          variant="outlined"
          size="small"
          onClick={() => onDeferClick(href)}
          style={{
            marginLeft: '4px',
            marginRight: '4px',
            verticalAlign: 'middle',
            borderRadius: '20px',
            borderColor: theme?.palette?.info?.main,
            color: theme?.palette?.info?.main,
          }}
        >
          {linkProps.children}
        </Button>
      );
    }
    return (
      <Link
        href={href}
        component="a"
        color="primary"
        target="_blank"
        rel="noopener noreferrer"
      >
        {linkProps.children}
      </Link>
    );
  },
});

// Helper: Get remediation steps text for grouped issues (with tabs)
const getGroupedRemediationStepsText = (
  remediationSteps: string,
  issues: any[] | undefined,
  catInfoRemediationSteps: string,
): string => {
  if (remediationSteps === 'resource-based') {
    return (
      issues?.[0]?._meta?.remediationSteps || 'No remediation steps available.'
    );
  }
  // If remediationSteps is not a known grouping strategy, treat it as actual text
  if (remediationSteps !== 'logical') {
    return remediationSteps;
  }
  return catInfoRemediationSteps;
};

// Helper: Get remediation steps text for single issues (fallback without autofix)
const getSingleIssueRemediationStepsText = (
  remediationSteps: string,
): string => {
  return remediationSteps || 'No remediation steps available.';
};

export const DefaultRemediationSteps = (
  props: DefaultRemediationStepsProps,
): ReactElement => {
  const classes = useStyles();
  let stepsText: string;
  const catInfoRemediationSteps =
    'Use the [software template](https://portal.developer.principal.com/generate/templates/principalinformationservices-emu/principal-developer-portal-templates-create-catalog-info) to update the identified missing and/or invalid values or create a catalog-info.yaml file if one does not exist.';

  const {
    issues,
    remediationSteps,
    onDeferClick,
    deferState = 'idle',
    deferEligible,
    catalogError,
    onRetryEligibility,
  } = props;
  const theme = useTheme();

  if (deferState === 'loading') {
    return (
      <Box display="flex" alignItems="center" style={{ padding: '8px 0' }}>
        <CircularProgress size={20} />
        <Typography
          className={classes.remediationDescription}
          style={{ marginLeft: '8px', marginBottom: 0, display: 'inline' }}
        >
          Submitting defer action...
        </Typography>
      </Box>
    );
  }

  if (issues && issues.length > 0) {
    stepsText = getGroupedRemediationStepsText(
      remediationSteps,
      issues,
      catInfoRemediationSteps,
    );
  } else {
    // For single issues or other cases
    stepsText = getSingleIssueRemediationStepsText(remediationSteps);
  }

  const linkRenderer = createMarkdownLinkRenderer(
    onDeferClick,
    deferState,
    theme,
    deferEligible,
    catalogError,
  );

  return (
    <>
      {catalogError && (
        <Box className={classes.budgetOwnerNoteError}>
          <ErrorOutlineIcon
            style={{
              fontSize: '18px',
              color: theme.palette.error.main,
              flexShrink: 0,
            }}
          />
          <Typography component="span" className={classes.budgetOwnerNoteText}>
            Could not check defer eligibility. Service unavailable.
          </Typography>
          {onRetryEligibility && (
            <Button
              size="small"
              variant="outlined"
              onClick={onRetryEligibility}
              style={{
                marginLeft: 'auto',
                borderRadius: '20px',
                borderColor: theme.palette.error.main,
                color: theme.palette.error.main,
                flexShrink: 0,
              }}
            >
              Retry
            </Button>
          )}
        </Box>
      )}
      {deferEligible === false && (
        <Box className={classes.budgetOwnerNote}>
          <InfoOutlinedIcon
            style={{
              fontSize: '22px',
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          />
          <Typography className={classes.budgetOwnerNoteText}>
            Budget updates can only be made by account owners.
          </Typography>
        </Box>
      )}
      <Typography
        className={classes.remediationDescription}
        data-testid="Remediation steps content"
        style={{ wordBreak: 'break-word' }}
      >
        <ReactMarkdown components={linkRenderer}>{stepsText}</ReactMarkdown>
      </Typography>
    </>
  );
};

// eslint-disable-next-line max-lines-per-function
export const ModalContentCard = (props: ContentCardProps): ReactElement => {
  const classes = useStyles();
  const analytics = useAnalytics();
  const identityApi = useApi(identityApiRef);
  const yardstickApi = useApi(yardstickApiRef);
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const [deferStates, setDeferStates] = useAtom(deferStateAtom);

  const {
    remediationSteps,
    issueDescription,
    issueName,
    resourceID,
    resourceName,
    region,
    group,
    issues,
    issueId,
    autoFix,
    entityRef,
    checkId,
  } = props;

  // Pre-flight catalog eligibility check (only runs for budget-compliance-check)
  const {
    eligible: deferEligible,
    catalogError: deferCatalogError,
    retry: retryDeferEligibility,
  } = useDeferEligibility(entityRef, checkId);

  // State to track active tab (0 = Automated, 1 = Manual)
  const [activeTab, setActiveTab] = useState<number>(0);

  // Derive defer state from atom (persists across modal open/close)
  const deferKey = issueId ?? `${entityRef}:${checkId}`;
  const deferState: DeferState = deferStates[deferKey] ?? 'idle';

  const setDeferState = useCallback(
    (state: DeferState) => {
      setDeferStates(prev => ({ ...prev, [deferKey]: state }));
    },
    [deferKey, setDeferStates],
  );

  // For grouped issues, find the autoFix config from the matching issue by ID
  // For resource-based grouping, use the first issue's autoFix config (all share same config)
  // For single issues, use the autoFix prop directly
  const autofixConfigToUse: YardstickAutoFix | undefined =
    autoFix ||
    (remediationSteps === 'resource-based'
      ? issues?.find((issue: any) => issue.autoFix !== undefined)?.autoFix
      : undefined) ||
    issues?.find((issue: any) => issue.id === issueId)?.autoFix;

  // For grouped issues, get the checkId from the specific issue OR use the parent checkId
  const effectiveCheckId =
    issues?.find((issue: any) => issue.id === issueId)?.checkId || checkId;

  // Gate the automated-remediation tab: shown whenever an autofix config exists,
  // except for Endor SCA findings, which additionally require the
  // SCA-remediation feature flag to be enabled for the current user.
  const showRemediation = shouldShowRemediation(
    { hasAutoFix: Boolean(autofixConfigToUse), checkId: effectiveCheckId },
    flagKey => featureFlagsApi.isActive(flagKey),
  );

  // Extract issue IDs for resource-grouped execution
  const issueIds =
    remediationSteps === 'resource-based' && (issues ?? []).length > 0
      ? (issues ?? [])
          .map((issue: any) => issue.id)
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      : undefined;

  // Get user information for analytics
  const getUserInfo = useCallback(async (): Promise<string> => {
    try {
      const { displayName, email } = await identityApi.getProfileInfo();
      return displayName || email?.replace(/@.*/, '') || 'unknown';
    } catch {
      return 'unknown';
    }
  }, [identityApi]);

  // Handle defer button click for budget compliance issues
  const handleDeferClick = useCallback(
    async (deferEndpoint: string): Promise<void> => {
      setDeferState('loading');
      try {
        await yardstickApi.deferBudget({
          deferEndpoint,
          entityRef: entityRef || '',
        });
        setDeferState('success');
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error('Failed to defer budget action:', error);
        if (
          error?.code === 'NOT_AUTHORIZED' ||
          error?.message === 'NOT_AUTHORIZED'
        ) {
          setDeferState('not-authorized');
        } else {
          setDeferState('error');
        }
      }
    },
    [setDeferState, yardstickApi, entityRef],
  );

  // Handle tab change with analytics
  const handleTabChange = async (tabIndex: number): Promise<void> => {
    if (tabIndex !== activeTab) {
      setActiveTab(tabIndex);

      try {
        const user = await getUserInfo();

        analytics.captureEvent(
          'insights:auto_remediation:tab_switch',
          'auto_remediation',
          {
            attributes: {
              entity: entityRef || 'unknown',
              checkId: checkId || 'unknown',
              issueId: issueId || 'unknown',
              fromTab: activeTab === 0 ? 'automated' : 'manual',
              toTab: tabIndex === 0 ? 'automated' : 'manual',
              user,
            },
          },
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('failed to capture analytic event:', error);
      }
    }
  };

  // Render tabs UI with automated/manual options
  const renderTabs = (testIdSuffix: string = '') => (
    <Box className={classes.customTabsContainer}>
      <button
        type="button"
        onClick={() => handleTabChange(0)}
        className={`${classes.customTab} ${
          activeTab === 0 ? classes.customTabActive : ''
        }`}
        data-testid={`automated-tab${testIdSuffix ? `-${testIdSuffix}` : ''}`}
      >
        <Box className={classes.customTabWithIcon}>
          <img src={SparklesIcon} alt="" className={classes.customTabIcon} />
          <span>Automated</span>
        </Box>
      </button>
      <button
        type="button"
        onClick={() => handleTabChange(1)}
        className={`${classes.customTab} ${
          activeTab === 1 ? classes.customTabActive : ''
        }`}
        data-testid={`manual-tab${testIdSuffix ? `-${testIdSuffix}` : ''}`}
      >
        Manual
      </button>
    </Box>
  );

  // Render manual tab content with remediation steps (for grouped issues with tabs)
  const renderManualTabContent = () => {
    return (
      <Box className={classes.tabPanel}>
        <DefaultRemediationSteps
          issues={issues ?? []}
          remediationSteps={remediationSteps}
          checkId={checkId}
          onDeferClick={handleDeferClick}
          deferState={deferState}
          deferEligible={deferEligible}
          catalogError={deferCatalogError}
          onRetryEligibility={retryDeferEligibility}
        />
      </Box>
    );
  };

  // Render automated tab content with AutoFix component
  const renderAutomatedTabContent = (
    testIdSuffix: string = '',
    isResourceGrouped: boolean = false,
  ) => (
    <Box
      className={classes.tabPanel}
      data-testid={`autofix-v2${testIdSuffix ? `-${testIdSuffix}` : ''}`}
    >
      <AutofixV2Component
        issueId={issueId}
        autofixConfig={autofixConfigToUse}
        remediationSteps={remediationSteps}
        entityRef={entityRef}
        checkId={effectiveCheckId}
        issueIds={issueIds}
        isResourceGrouped={isResourceGrouped}
      />
    </Box>
  );

  // Render tabbed remediation section (when autofix is available)
  const renderTabbedRemediation = (
    testIdSuffix: string = '',
    isResourceGrouped: boolean = false,
  ) => (
    <Box>
      <Box className={classes.remediationHeader}>
        <Typography
          variant="h3"
          className={classes.remediationSteps}
          data-testid="Remediation steps title"
          style={{ paddingRight: 0, marginBottom: 0 }}
        >
          Remediation Steps
        </Typography>
        {renderTabs(testIdSuffix)}
      </Box>
      {activeTab === 0 &&
        renderAutomatedTabContent(testIdSuffix, isResourceGrouped)}
      {activeTab === 1 && renderManualTabContent()}
    </Box>
  );

  // Render fallback remediation section (when no autofix available - for single issues)
  const renderFallbackRemediation = () => {
    if (deferState === 'success' && checkId === 'budget-compliance-check') {
      return (
        <>
          <Typography
            variant="h3"
            className={classes.remediationSteps}
            data-testid="Remediation steps title"
          >
            Remediation Steps
          </Typography>
          <Typography
            className={classes.remediationDescription}
            data-testid="defer-success-message"
            style={{ wordBreak: 'break-word' }}
          >
            Budget action for {entityRef} has been deferred successfully.
          </Typography>
        </>
      );
    }
    return (
      <>
        <Typography
          variant="h3"
          className={classes.remediationSteps}
          data-testid="Remediation steps title"
        >
          Remediation Steps
        </Typography>
        <DefaultRemediationSteps
          issues={issues ?? []}
          remediationSteps={remediationSteps}
          checkId={checkId}
          onDeferClick={handleDeferClick}
          deferState={deferState}
          deferEligible={deferEligible}
          catalogError={deferCatalogError}
          onRetryEligibility={retryDeferEligibility}
        />
      </>
    );
  };

  return (
    <Box className={classes.rootContainer}>
      {group ? (
        <Box className={classes.contentBoxContainer} tabIndex={0}>
          <Box className={classes.issueDescription}>
            <Typography
              variant="h3"
              className={classes.issueDetails}
              data-testid="Findings title"
            >
              Finding Details
            </Typography>
            {remediationSteps !== 'resource-based' && (
              <>
                <Typography
                  className={classes.issueDescriptionDetail}
                  data-testid="Findings content"
                  style={{ wordBreak: 'break-word' }}
                >
                  {issueDescription}
                </Typography>
                <LogicalAccordion
                  title="Logical Issues"
                  issues={issues || []}
                />
                <Divider />
              </>
            )}
            {remediationSteps === 'resource-based' && (
              <>
                {issues?.[0]?._meta?.resourceArn &&
                  issues?.[0]?._meta?.resourceArn !== 'No ARN available' && (
                    <Typography
                      className={classes.issueDescriptionDetail}
                      data-testid="Findings content"
                      style={{ wordBreak: 'break-word' }}
                    >
                      <strong>{issues?.[0]?._meta?.issueName}</strong>
                    </Typography>
                  )}
                <ResourceBasedAccordion
                  title="Resource Issues"
                  issues={issues || []}
                />
                <Typography
                  className={classes.issueDescriptionDetail}
                  data-testid="Issue description"
                  style={{ wordBreak: 'break-word' }}
                >
                  <ReactMarkdown components={markdownLinkRenderer}>
                    {issueDescription || 'No issue description available.'}
                  </ReactMarkdown>
                </Typography>
                <Divider />
              </>
            )}
            {/* Remediation section - tabbed if autofix available, fallback otherwise */}
            {remediationSteps === 'resource-based' && autofixConfigToUse
              ? renderTabbedRemediation('resource', true)
              : renderFallbackRemediation()}
            <Divider />
          </Box>
        </Box>
      ) : (
        <Box className={classes.contentBoxContainer} tabIndex={0}>
          <Box className={classes.issueDescription}>
            <Typography
              variant="h3"
              className={classes.issueDetails}
              data-testid="Findings title"
            >
              Finding details
            </Typography>
            <Typography
              className={classes.issueDescriptionDetail}
              data-testid="Findings content"
              style={{ wordBreak: 'break-word' }}
            >
              {resourceID && resourceID !== 'No ARN available' ? (
                <>
                  <strong>{issueName}</strong>
                  <br />
                  <ReactMarkdown components={markdownLinkRenderer}>
                    {issueDescription || 'No issue description available.'}
                  </ReactMarkdown>
                  <br />
                  <strong>Resource ID:</strong> {resourceID}
                  {resourceName && (
                    <>
                      <br />
                      <strong>Resource Name:</strong> {resourceName}
                    </>
                  )}
                  {region && (
                    <>
                      <br />
                      <strong>Region:</strong> {region}
                    </>
                  )}
                </>
              ) : (
                <ReactMarkdown components={markdownLinkRenderer}>
                  {issueDescription || 'No issue description available.'}
                </ReactMarkdown>
              )}
            </Typography>
            <Divider />
          </Box>
          <Box className={classes.actionSteps}>
            {/* Remediation section - tabbed if autofix available, fallback otherwise */}
            {issueId && autofixConfigToUse && showRemediation
              ? renderTabbedRemediation()
              : renderFallbackRemediation()}
            <Divider />
          </Box>
        </Box>
      )}
    </Box>
  );
};
